import React, { useState, useCallback, useMemo } from 'react';
import { Box } from '@mui/system';
import { ConfigurationButton, Loading, MultiSelectControlledOption } from 'components';
import { getLoggedUserData } from 'utils';
import { SiteSelectionPanel } from '../siteSelectionPanel';
import { ScorecardContent } from '../scorecardContent';
import { ScorecardFilters } from '../scorecardFilters';
import { AddNewRowButton, UploadDataDropdownButton, TemplateActionsButton, UploadUtilityDataButton } from '../buttons';

interface ScorecardLayoutProps {
    selectedPeriod: string;
    sitesData?: any;
    isLoading?: boolean;
}

interface FilterValues {
    selectedFiscalYears: MultiSelectControlledOption[];
    selectedToggleOption: 'INPUT_UNIT' | 'DEFAULT_UNIT';
}

export const ScorecardLayout = ({ selectedPeriod, sitesData, isLoading }: ScorecardLayoutProps) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [filterValues, setFilterValues] = useState<FilterValues>({
        selectedFiscalYears: [],
        selectedToggleOption: 'INPUT_UNIT',
    });

    const loggedUserData = getLoggedUserData();
    const userRoles = loggedUserData.roles || [];
    const isAdmin = userRoles.some(
        (rol: string) => rol === 'MODULE_ADMIN' || rol === 'SUPER_ADMIN'
    );

    const isUploadDataButtonsDisabled = useMemo(() => !(filterValues.selectedToggleOption === 'INPUT_UNIT'), [filterValues.selectedToggleOption]);

    const handleFiltersChange = useCallback((filters: FilterValues) => {
        setFilterValues(filters);
    }, []);

    if (isLoading) return <Loading />;

    return (
        <Box sx={{ padding: '24px' }}>
            {/* shared across all tabs */}
            <SiteSelectionPanel sitesData={sitesData} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '38px', marginBottom: '11px' }}>
                <Box sx={{ display: 'flex', gap: '12px', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <ScorecardFilters onFiltersChange={handleFiltersChange} disabledToggle={isEditMode} />
                    <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <UploadUtilityDataButton disabled={isUploadDataButtonsDisabled} />
                        <UploadDataDropdownButton
                            filterValues={filterValues}
                            selectedPeriod={selectedPeriod}
                            disabled={isUploadDataButtonsDisabled}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '12px', flexDirection: 'column', alignItems: 'flex-end', alignSelf: 'flex-end', marginBottom: isEditMode ? '0px' : '48px' }}>
                    {isAdmin && <ConfigurationButton onEditMode={isEditMode} onToggleEditMode={() => setIsEditMode(!isEditMode)} disabled={filterValues.selectedToggleOption === 'DEFAULT_UNIT'} />}
                    {isEditMode && <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <AddNewRowButton />
                        <TemplateActionsButton />
                    </Box>}
                </Box>
            </Box>
            {/* Period-specific content */}
            <ScorecardContent
                selectedPeriod={selectedPeriod}
                filterValues={filterValues}
                isAdmin={isAdmin}
                isEditMode={isEditMode}
                userRoles={userRoles}
            />
        </Box>
    );
};